import { get } from 'svelte/store';
import type { IAnswer } from '../interfaces/answer.interface';
import {
  state_conversationResults,
  state_questionImageSearch,
} from '../store/state';
import { SessionStoreHandler } from './session-storage-handler';

export const ConversationHistoryHelper = {
  addQuestion: (
    message: string,
    isGreeting: boolean,
    isStreaming: boolean
  ): IAnswer => {
    const currentConversationResult: IAnswer[] = get(state_conversationResults);
    const questionFiles = get(state_questionImageSearch)
      .filter((attachment) => attachment.status === 'ready' && attachment.id)
      .map((attachment) => ({
        id: attachment.id!,
      }));
    const questionOnlyItem: IAnswer = {
      question: message,
      thread_id: '',
      answer: '',
      answer_chunks: [],
      tool_calls: undefined,
      question_id: '',
      document_data: undefined,
      document_id: undefined,
      is_streaming: isStreaming,
      is_streamCancelled: false,
      is_greeting: isGreeting,
      has_unfinished_citation: false,
      position_in_thread: currentConversationResult.length,
      question_files: questionFiles.length ? questionFiles : undefined,
    };
    state_conversationResults.set([
      ...currentConversationResult,
      questionOnlyItem,
    ]);
    return questionOnlyItem;
  },
  stopStreaming: (index: number, isFinished: boolean): void => {
    const currentConversationResults = get(state_conversationResults);
    //const index: number = currentConversationResults.length - 1;
    if (currentConversationResults[index]?.is_streaming) {
      currentConversationResults[index].is_streaming = false;
      if (!isFinished)
        currentConversationResults[index].is_streamCancelled = true;
      state_conversationResults.set([...currentConversationResults]);
    }
  },
  cancelUnansweredTurns: (): string[] => {
    const cancelledClientMessageIds: string[] = [];
    const conversationItems = get(state_conversationResults).map(
      (item, index) => {
        if (
          item.question &&
          (!item.question_id || item.is_streaming) &&
          !item.is_greeting &&
          !item.is_streamCancelled &&
          !item.hideOnChat
        ) {
          if (item.client_message_id) {
            cancelledClientMessageIds.push(item.client_message_id);
          }
          return {
            ...item,
            is_streaming: false,
            is_streamCancelled: true,
            position_in_thread: index,
          };
        }
        return { ...item, position_in_thread: index };
      }
    );
    state_conversationResults.set(conversationItems);
    return cancelledClientMessageIds;
  },
  isEmpty: (): boolean => {
    return get(state_conversationResults).length === 0;
  },
  getLastTurn: (): IAnswer => {
    return get(state_conversationResults)[
      get(state_conversationResults).length - 1
    ];
  },
  updateLastTurn: (turn: IAnswer): void => {
    const conversationItem = [...get(state_conversationResults)];
    conversationItem[conversationItem.length - 1] = { ...turn };
    state_conversationResults.set([...conversationItem]);
    if (turn.question_id && !turn.is_greeting) {
      SessionStoreHandler.update({
        lastQId: turn.question_id,
        threadId: turn.thread_id ? turn.thread_id : turn.question_id,
        lastQUrl: window?.location?.href,
      });
    }
  },
  updateTurn: (turn: IAnswer): void => {
    const conversationItems = [...get(state_conversationResults)];
    const index = conversationItems.findIndex(
      (item) =>
        (!!turn.client_message_id &&
          item.client_message_id === turn.client_message_id) ||
        (!!turn.request_id && item.request_id === turn.request_id) ||
        (!!turn.question_id && item.question_id === turn.question_id)
    );
    const fallbackIndex =
      typeof turn.position_in_thread === 'number'
        ? turn.position_in_thread
        : conversationItems.length - 1;
    const updateIndex = index >= 0 ? index : fallbackIndex;

    if (updateIndex < 0 || updateIndex >= conversationItems.length) return;

    conversationItems[updateIndex] = {
      ...turn,
      position_in_thread: updateIndex,
    };
    state_conversationResults.set([...conversationItems]);
    if (turn.question_id && !turn.is_greeting) {
      SessionStoreHandler.update({
        lastQId: turn.question_id,
        threadId: turn.thread_id ? turn.thread_id : turn.question_id,
        lastQUrl: window?.location?.href,
      });
    }
  },
  hideLastTurnOnError: (e: Error): void => {
    if (e?.name === 'AbortError') return;

    const conversationItem = [...get(state_conversationResults)];

    if (conversationItem.length === 0) return;

    conversationItem[conversationItem.length - 1] = {
      ...conversationItem[conversationItem.length - 1],
      hideOnChat: true,
    };
    state_conversationResults.set([...conversationItem]);
  },
  getLastAnsweredId: (): string | null => {
    return SessionStoreHandler.get()?.lastQId;
  },
  getThreadId: (): string | null => {
    return SessionStoreHandler.get()?.threadId;
  },
  populateThread: (answers: IAnswer[]): void => {
    state_conversationResults.set([...answers]);
  },
  clear: (): void => {
    SessionStoreHandler.remove('threadId');
    SessionStoreHandler.remove('lastQId');
    SessionStoreHandler.remove('lastQUrl');
    state_conversationResults.set([]);
  },
  getUnanswered: (): {
    respondedToId: string | null;
    unrespondedCompilation: string | null;
    questionsSoFar: number;
  } => {
    const conversationItems = get(state_conversationResults).filter(
      (item) => !item.is_streamCancelled && !item.is_streamError
    );
    const visibleConversationItems = conversationItems.filter(
      (item) => !item.hideOnChat
    );
    const lastIndex = conversationItems
      .map((item, index) => (item?.question_id ? index : -1))
      .reduce((max, value) => Math.max(max, value), -1);
    if (lastIndex == -1) {
      // no previously answered question or greeting
      if (visibleConversationItems.length > 0) {
        return {
          respondedToId: null,
          unrespondedCompilation: visibleConversationItems
            .map((item) => item.question)
            .join('\n'),
          questionsSoFar: 0,
        };
      } else {
        return {
          respondedToId: null,
          unrespondedCompilation: null,
          questionsSoFar: 0,
        };
      }
    } else {
      // previous answer they're responding to
      const responseAnchor = conversationItems[lastIndex];
      const unansweredVisibleQuestions = conversationItems
        .slice(lastIndex + 1)
        .filter((item) => !item.hideOnChat);
      if (unansweredVisibleQuestions.length == 0) {
        return {
          respondedToId: responseAnchor.question_id,
          unrespondedCompilation: null,
          questionsSoFar: visibleConversationItems.length,
        };
      } else if (unansweredVisibleQuestions.length > 0) {
        return {
          respondedToId: responseAnchor.question_id,
          unrespondedCompilation: unansweredVisibleQuestions
            .map((item) => item.question)
            .join('\n'),
          questionsSoFar: visibleConversationItems.length,
        };
      } else {
        return {
          respondedToId: null,
          unrespondedCompilation: null,
          questionsSoFar: 0,
        };
      }
    }
  },
};
